import React, { useMemo, useState, useEffect, useRef, forwardRef, memo } from "react";
import {
	Box,
	Typography,
	TextField,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Button,
	Chip,
	IconButton,
	Card,
	CardContent,
	Dialog,
	DialogContent,
	Divider,
	Paper,
	LinearProgress,
	Skeleton,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HTMLFlipBook from "react-pageflip";
import * as pdfjsLib from "pdfjs-dist";
// ----- * ICONS * -----
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ShareIcon from "@mui/icons-material/Share";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DownloadIcon from "@mui/icons-material/Download";
import SortIcon from "@mui/icons-material/Sort";

// ============================================================
// PDF CACHE / HELPERS
// ============================================================

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.530/build/pdf.worker.min.mjs";
const TEMP_PUBLIC_SHARE_URL = "https://vmsprojectdata.s3.amazonaws.com/public/documents/1779099427297-Apr_2026.pdf";

const LRU_LIMIT = 8;
const NEIGHBOR_RADIUS = 2;
const pdfDocCache = new Map();
const pdfDocPromiseCache = new Map();

const getCachedPdfDoc = (fileUrl) => {
	if (!fileUrl) return null;
	const entry = pdfDocCache.get(fileUrl);
	if (!entry) return null;
	pdfDocCache.delete(fileUrl);
	pdfDocCache.set(fileUrl, entry);
	return entry;
};

const setCachedPdfDoc = (fileUrl, doc) => {
	if (!fileUrl || !doc) return;
	if (pdfDocCache.has(fileUrl)) pdfDocCache.delete(fileUrl);
	pdfDocCache.set(fileUrl, { doc });

	while (pdfDocCache.size > 8) {
		const oldestKey = pdfDocCache.keys().next().value;
		if (!oldestKey) break;
		const oldest = pdfDocCache.get(oldestKey);
		try {
			oldest?.doc?.cleanup?.();
		} catch (_) { }
		pdfDocCache.delete(oldestKey);
	}
};

const loadPdfDocWithCache = (fileUrl, onProgress) => {
	const cached = getCachedPdfDoc(fileUrl);
	if (cached?.doc) return Promise.resolve(cached.doc);

	const inflight = pdfDocPromiseCache.get(fileUrl);
	if (inflight) return inflight;

	const task = pdfjsLib.getDocument({
		url: fileUrl,
		disableRange: false,
		disableStream: false,
		disableAutoFetch: false,
		rangeChunkSize: 1024 * 1024,
		useWorkerFetch: true,
		cMapPacked: true,
		isImageDecoderSupported: false,
		isOffscreenCanvasSupported: false,
		useWasm: false,
	});

	if (typeof onProgress === "function") {
		task.onProgress = ({ loaded = 0, total = 0 }) => {
			if (!total) return;
			onProgress(Math.max(1, Math.min(100, Math.round((loaded / total) * 100))));
		};
	}

	const promise = task.promise.then((doc) => {
		setCachedPdfDoc(fileUrl, doc);
		return doc;
	}).finally(() => {
		pdfDocPromiseCache.delete(fileUrl);
	});

	pdfDocPromiseCache.set(fileUrl, promise);
	return promise;
};

class LRUCache {
	constructor(limit = 8, onEvict = () => { }) {
		this.limit = limit;
		this.onEvict = onEvict;
		this.map = new Map();
	}

	get(key) {
		if (!this.map.has(key)) return undefined;
		const value = this.map.get(key);
		this.map.delete(key);
		this.map.set(key, value);
		return value;
	}

	set(key, value) {
		if (this.map.has(key)) this.map.delete(key);
		this.map.set(key, value);

		if (this.map.size > this.limit) {
			const oldestKey = this.map.keys().next().value;
			const oldestValue = this.map.get(oldestKey);
			this.map.delete(oldestKey);
			this.onEvict(oldestValue);
		}
	}

	clear() {
		this.map.forEach((value) => this.onEvict(value));
		this.map.clear();
	}
}

const getVirtualWindow = (page, total, radius = NEIGHBOR_RADIUS) => {
	const pages = new Set();
	for (let p = Math.max(1, page - radius); p <= Math.min(total, page + radius); p += 1) {
		pages.add(p);
	}
	return pages;
};

const usePdfDocument = ({ fileUrl, isOpen }) => {
	const [pdfDoc, setPdfDoc] = useState(null);
	const [numPages, setNumPages] = useState(0);
	const [progress, setProgress] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (!isOpen || !fileUrl) {
			setPdfDoc(null);
			setNumPages(0);
			setProgress(0);
			setIsLoading(false);
			return;
		}

		let cancelled = false;
		setIsLoading(true);
		setError("");
		setNumPages(0);
		setProgress(0);

		loadPdfDocWithCache(fileUrl, setProgress)
			.then((doc) => {
				if (cancelled) return;
				setPdfDoc(doc);
				setNumPages(doc?.numPages || 0);
			})
			.catch((err) => {
				if (!cancelled) setError(err?.message || "Failed to load PDF.");
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});

		return () => {
			cancelled = true;
			setPdfDoc(null);
			setNumPages(0);
			setProgress(0);
			setIsLoading(false);
		};
	}, [fileUrl, isOpen]);

	return { pdfDoc, numPages, progress, isLoading, error };
};

const PdfCanvasPageBase = forwardRef(function PdfCanvasPageBase({
	pdfDoc,
	pageNumber,
	width,
	shouldRender,
	cacheRef,
	onRendered,
	quality = "balanced",
	onPageClick,
	clickable = false,
}, pageRef) {
	const canvasRef = useRef(null);
	const [status, setStatus] = useState("idle");
	const cacheKey = `${pageNumber}-${Math.round(width)}`;

	useEffect(() => {
		if (!pdfDoc || !canvasRef.current || !shouldRender) return undefined;

		let cancelled = false;
		let renderTask = null;
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d", { alpha: false });
		const cached = cacheRef.current.get(cacheKey);

		const hydrateFromCache = () => {
			if (!cached?.bitmap || cancelled) return false;
			canvas.width = cached.width;
			canvas.height = cached.height;
			ctx.drawImage(cached.bitmap, 0, 0);
			setStatus("ready");
			return true;
		};

		const renderPage = async () => {
			setStatus("loading");

			if (hydrateFromCache()) {
				onRendered?.(pageNumber);
				return;
			}

			try {
				const page = await pdfDoc.getPage(pageNumber);
				if (cancelled) return;

				const viewport = page.getViewport({ scale: 1 });
				const scale = width / viewport.width;
				const scaledViewport = page.getViewport({ scale });
				const dpr = Math.min(quality === "fast" ? 1 : 1.35, window.devicePixelRatio || 1);

				canvas.width = Math.floor(scaledViewport.width * dpr);
				canvas.height = Math.floor(scaledViewport.height * dpr);
				canvas.style.width = `${Math.floor(scaledViewport.width)}px`;
				canvas.style.height = `${Math.floor(scaledViewport.height)}px`;
				ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

				renderTask = page.render({ canvasContext: ctx, viewport: scaledViewport });
				await renderTask.promise;
				if (cancelled) return;

				cacheRef.current.set(cacheKey, { bitmap: null, width: canvas.width, height: canvas.height });

				const doIdleCache = async () => {
					if (!window.createImageBitmap || cancelled) return;
					const bitmap = await createImageBitmap(canvas);

					if (cancelled) {
						bitmap.close?.();
						return;
					}

					cacheRef.current.set(cacheKey, { bitmap, width: canvas.width, height: canvas.height });
				};

				if (window.requestIdleCallback) {
					window.requestIdleCallback(() => doIdleCache().catch(() => { }));
				} else {
					setTimeout(() => doIdleCache().catch(() => { }), 0);
				}

				page.cleanup();
				setStatus("ready");
				onRendered?.(pageNumber);
			} catch (err) {
				if (!cancelled) setStatus("error");
			}
		};

		renderPage();

		return () => {
			cancelled = true;
			if (renderTask) {
				try {
					renderTask.cancel();
				} catch (_) { }
			}
		};
	}, [pdfDoc, pageNumber, width, shouldRender, cacheKey, cacheRef, onRendered, quality]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas || shouldRender) return;
		canvas.width = 0;
		canvas.height = 0;
		setStatus("idle");
	}, [shouldRender]);

	return (
		<Box ref={pageRef} onClick={onPageClick} className="page" sx={{
			width: `${Math.round(width)}px`,
			height: `${Math.round(width * 1.42)}px`,
			border: "1px solid #dbe6f5",
			bgcolor: "#fff",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			overflow: "hidden",
			cursor: clickable ? "pointer" : "default",
			flexShrink: 0,
		}}>
			{status !== "ready" && status !== "error" ? <DarpanPageSkeleton /> : null}
			{status === "error" ? (
				<Typography sx={{ color: "#b91c1c", px: 2, fontSize: "0.85rem" }}>
					Failed to render page {pageNumber}
				</Typography>
			) : null}
			<canvas ref={canvasRef} style={{
				display: status === "ready" ? "block" : "none",
				width: "100%",
				height: "100%",
			}} />
		</Box>
	);
});

const PdfCanvasPage = memo(PdfCanvasPageBase);

// -------------- DATA ----------------

const darpanDocuments = [
	{
		id: "aug-2026",
		title: "August 2026",
		month: 8,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1788952240675-Bulletin_Aug_2026.pdf",
		shareUrl: "/?darpan=aug-2026",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1788952355761-BJS_Darpan_Cover_Aug_2026.jpg",
	},
	{
		id: "july-2026",
		title: "July 2026",
		month: 7,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1784896721336-News_Bulletin_July_2026_16_1.pdf",
		shareUrl: "/?darpan=july-2026",
		thumbnail: "https://vmsprojectdata.s3.ap-south-1.amazonaws.com/public/profile-images/1784896612325-July%202026%20Cover.jpg",
	},
	{
		id: "june-2026",
		title: "June 2026",
		month: 6,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1781762632785-BJS_News_Bulletin_June_2026.pdf",
		shareUrl: "/?darpan=june-2026",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1781762649444-BJS_News_Bulletin_June_2026_img.jpg",
	},
	{
		id: "may-2026",
		title: "May 2026",
		month: 5,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1779099496808-May_2026.pdf",
		shareUrl: "/?darpan=may-2026",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1779100012194-Cover-Page_1.jpg",
	},
	{
		id: "apr-2026",
		title: "April 2026",
		month: 4,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1779099427297-Apr_2026.pdf",
		shareUrl: "/?darpan=apr-2026",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1778949199645-BJS_News_Bulletin_April_2026_img.jpg",
	},
	{
		id: "mar-2026",
		title: "March 2026",
		month: 3,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1779078022749-BJS_News_Bulletin_Mar_2026.pdf",
		shareUrl: "/?darpan=mar-2026",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1778949177455-BJS_News_Bulletin_March_2026_img.jpg",
	},
	{
		id: "feb-2026",
		title: "February 2026",
		month: 2,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1778950975556-BJS_News_Bulletin_Feb_2026_16.pdf",
		shareUrl: "/?darpan=feb-2026",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1778949154459-BJS-Newsletter-Feb-2026.jpg",
	},
	{
		id: "jan-2026",
		title: "January 2026",
		month: 1,
		year: 2026,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1779099302575-Jan_2026.pdf",
		shareUrl: "/?darpan=jan-2026",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1778949129490-BJS-Newsletter-Jan-2026-32-v2.jpg",
	},
	{
		id: "dec-2025",
		title: "December 2025",
		month: 12,
		year: 2025,
		file: "https://vmsprojectdata.s3.amazonaws.com/public/documents/1779099261917-Dec_2025.pdf",
		shareUrl: "/?darpan=dec-2025",
		thumbnail: "https://vmsprojectdata.s3.amazonaws.com/public/images/1779083736477-Dec_2025.png",
	},
];

const DarpanPageSkeleton = ({ width = "100%", height = "100%", compact = false }) => (
	<Box
		sx={{
			width,
			height,
			p: compact ? 1.1 : 2,
			bgcolor: "#ffffff",
			display: "grid",
			gridTemplateColumns: compact ? "1fr" : "1.45fr 0.8fr",
			gap: compact ? 0.9 : 1.5,
			overflow: "hidden",
		}}
	>
		<Box sx={{ minWidth: 0 }}>
			<Skeleton variant="text" width="72%" height={compact ? 16 : 24} animation="wave" />
			<Skeleton variant="text" width="46%" height={compact ? 12 : 18} animation="wave" />
			<Box sx={{ mt: compact ? 0.5 : 1.2 }}>
				{Array.from({ length: compact ? 9 : 18 }).map((_, index) => (
					<Skeleton
						key={`line-${index}`}
						variant="text"
						width={`${index % 4 === 3 ? 72 : index % 5 === 2 ? 86 : 96}%`}
						height={compact ? 10 : 15}
						animation="wave"
					/>
				))}
			</Box>
		</Box>
		{!compact ? (
			<Box sx={{ minWidth: 0 }}>
				<Skeleton variant="rounded" width="70%" height={58} sx={{ ml: "auto", mb: 1.5 }} animation="wave" />
				{Array.from({ length: 3 }).map((_, index) => (
					<Box key={`media-${index}`} sx={{ mb: 1.4 }}>
						<Skeleton variant="rounded" width="100%" height={70} animation="wave" />
						<Skeleton variant="text" width="88%" height={12} animation="wave" />
						<Skeleton variant="text" width="68%" height={12} animation="wave" />
					</Box>
				))}
			</Box>
		) : null}
	</Box>
);



const LegacyFlipBookPage = () => {
	const [search, setSearch] = useState("");
	const [year, setYear] = useState("all");
	const [sort, setSort] = useState("newest");
	const [selectedMagazine, setSelectedMagazine] = useState(null);
	const [openViewer, setOpenViewer] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const [currentPage, setCurrentPage] = useState(1);
	const [readMode, setReadMode] = useState(true);
	const [zoomFactor, setZoomFactor] = useState(1);
	const [viewportWidth, setViewportWidth] = useState(
		typeof window !== "undefined" ? window.innerWidth : 1280
	);
	const [viewportHeight, setViewportHeight] = useState(
		typeof window !== "undefined" ? window.innerHeight : 900
	);
	const [isFullscreen, setIsFullscreen] = useState(false);

	const isSinglePageFlip = isFullscreen || isMobile;
	const viewerContainerRef = useRef(null);

	const cacheRef = useRef(
		new LRUCache(LRU_LIMIT, (entry) => {
			if (entry?.bitmap?.close) entry.bitmap.close();
		})
	);


	const flipbookRef = useRef(null);
	const years = [...new Set(darpanDocuments.map((item) => item.year))];
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" })
	}, [])

	const { pdfDoc, numPages, progress, isLoading, error } = usePdfDocument({
		fileUrl: selectedMagazine?.file || "",
		isOpen: openViewer,
	});

	const navigate = useNavigate();

	useEffect(() => {
		const onResize = () => {
			setViewportWidth(window.innerWidth);
			setViewportHeight(window.innerHeight);
		};

		window.addEventListener("resize", onResize);

		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		const onFullscreenChange = () => {
			setIsFullscreen(Boolean(document.fullscreenElement));
			setViewportWidth(window.innerWidth);
			setViewportHeight(window.innerHeight);
		};

		document.addEventListener("fullscreenchange", onFullscreenChange);

		return () => {
			document.removeEventListener("fullscreenchange", onFullscreenChange);
		};
	}, []);

	const pageWidth = useMemo(() => {
		const headerHeight = 44;
		const toolbarHeight = 78;

		const horizontalPadding = isMobile ? 20 : 40;
		const verticalPadding = isMobile ? 18 : 24;

		const availableWidth = Math.max(
			260,
			viewportWidth - horizontalPadding
		);

		const availableHeight = Math.max(
			320,
			viewportHeight -
			headerHeight -
			toolbarHeight -
			verticalPadding
		);

		const readWidth = Math.min(
			availableWidth,
			availableHeight / 1.42
		);

		const flipSpreadDivisor = isSinglePageFlip ? 1 : 2;

		const flipWidth = Math.min(
			availableWidth / flipSpreadDivisor,
			availableHeight / 1.42
		);

		const base = readMode ? readWidth : flipWidth;

		const zoomed = Math.round(base * zoomFactor);

		return Math.max(
			240,
			Math.min(1400, zoomed)
		);
	}, [
		isMobile,
		viewportWidth,
		viewportHeight,
		readMode,
		isSinglePageFlip,
		zoomFactor,
	]);

	const virtualPages = useMemo(
		() =>
			getVirtualWindow(
				currentPage,
				numPages,
				readMode ? 1 : NEIGHBOR_RADIUS
			),
		[currentPage, numPages, readMode]
	);

	// -------------------------------------------------------
	// FILTER + SORT
	// -------------------------------------------------------

	const filteredMagazines = useMemo(() => {
		let result = [...darpanDocuments];

		if (search.trim()) {
			const query = search.toLowerCase();

			result = result.filter((magazine) =>
				`${magazine.title} ${magazine.edition} ${magazine.year}`
					.toLowerCase()
					.includes(query)
			);
		}

		if (year !== "all") {
			result = result.filter(
				(magazine) => magazine.year === Number(year)
			);
		}

		result.sort((a, b) => {
			if (sort === "newest") {
				return b.year - a.year || b.month - a.month;
			}

			return a.year - b.year || a.month - b.month;
		});
		return result;
	}, [search, year, sort]);

	// -------------------------------------------------------
	// OPEN MAGAZINE
	// -------------------------------------------------------

	useEffect(() => {
		if (!openViewer) {
			cacheRef.current.clear();
			setCurrentPage(1);
			setZoomFactor(1);
			setReadMode(true);
		}
	}, [openViewer]);

	const handleOpenMagazine = (magazine) => {
		setSelectedMagazine(magazine);
		setOpenViewer(true);
		setReadMode(true);
		setCurrentPage(1);
	};

	const handleCloseViewer = () => {
		setOpenViewer(false);
		setSelectedMagazine(null);
	};


	// Navigation to pages 
	const goNextPage = () => {
		setCurrentPage((prev) => Math.min(numPages || 1, prev + 1));
		if (readMode) return;
		flipbookRef.current?.pageFlip()?.flipNext();
	};

	const goPrevPage = () => {
		setCurrentPage((prev) => Math.max(1, prev - 1));
		if (readMode) return;
		flipbookRef.current?.pageFlip()?.flipPrev();
	};

	useEffect(() => {
		if (readMode || !flipbookRef.current) return;
		const instance = flipbookRef.current.pageFlip?.();
		if (!instance) return;
		try {
			instance.turnToPage(
				Math.max(0, currentPage - 1)
			);
		} catch (_err) { }
	}, [readMode, currentPage]);


	const clearFilters = () => {
		setSearch("");
		setYear("all");
		setSort("newest");
	};

	const toggleFullscreen = async () => {
		try {
			const root = document.getElementById("bjs-darpan-viewer-root");
			if (!root) return;
			if (!document.fullscreenElement) {
				await root.requestFullscreen();
			} else {
				await document.exitFullscreen();
			}
		} catch (_err) { }
	};

	const downloadCurrentPdf = () => {
		if (!selectedMagazine?.file) return;
		const anchor = document.createElement("a");
		anchor.href = selectedMagazine.file;
		anchor.download = `${selectedMagazine.title || "BJS-Darpan"}.pdf`;
		anchor.rel = "noopener";
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
	};

	const getShareUrl = (doc) => {
		if (TEMP_PUBLIC_SHARE_URL) return TEMP_PUBLIC_SHARE_URL;
		if (!doc) return "";
		const origin = typeof window !== "undefined" ? window.location.origin : "";
		if (doc.shareUrl) {
			try {
				return new URL(doc.shareUrl, origin).href;
			} catch {
				return doc.shareUrl;
			}
		}
		if (doc.id) {
			return `${origin}/?darpan=${doc.id}`;
		}
		return doc.file || "";
	};

	const shareCurrentPdf = async () => {
		const title = selectedMagazine?.title || "BJS Darpan";
		const url = getShareUrl(selectedMagazine);
		if (!url) return;
		try {
			if (navigator.share) {
				await navigator.share({ title, url });
				return;
			}
		} catch (err) {
			// fall through to clipboard fallback
		}

		try {
			if (navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(url);
				window.alert("PDF link copied to clipboard.");
				return;
			}
		} catch (err) { }

		// Last resort: prompt the user to copy
		// eslint-disable-next-line no-alert
		window.prompt("Copy link to clipboard:", url);
	};

	return (
		<Box sx={{
			minHeight: "100vh",
			background: "linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)",
			px: { xs: 2, sm: 3, md: 5, lg: 8 },
			pt: { xs: 2, sm: 2.5, md: 3 },
			pb: { xs: 4, md: 6 },
		}}>
			<Box sx={{
				maxWidth: "1400px",
				mx: "auto",
				display: "flex",
				flexWrap: { xs: "wrap", sm: "nowrap" },
				alignItems: "center",
				justifyContent: "space-between",
				gap: 2,
				mb: { xs: 2.5, md: 3 },
			}}>
				{/* Left side: Back Button + Title */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<IconButton
						onClick={() => {
							if (window.history.length > 1) {
								navigate(-1);
							} else {
								navigate("/");
							}
						}}
						size="small"
						sx={{
							color: "#334155",
							p: 1,
							border: "1px solid rgba(148, 163, 184, 0.25)",
							bgcolor: "#ffffff",
							boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
							"&:hover": {
								bgcolor: "#f1f5f9",
								borderColor: "rgba(148, 163, 184, 0.4)",
							},
						}}
						aria-label="Back"
					>
						<ArrowBackIcon fontSize="small" />
					</IconButton>
					<Typography variant="h1" sx={{
						fontWeight: 700,
						fontSize: { xs: "20px", sm: "26px", md: "32px" },
						color: "#121E33",
					}}>Darpan</Typography>
				</Box>

				{/* Right side: Compact inline filters */}
				<Box sx={{
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					flexWrap: "wrap",
				}}>
					<FormControl size="small" sx={{ minWidth: { xs: 110, sm: 130 } }}>
						<InputLabel sx={{ fontSize: "14px" }}>Year</InputLabel>
						<Select
							value={year}
							label="Year"
							onChange={(e) => setYear(e.target.value)}
							sx={{
								bgcolor: "#ffffff",
								borderRadius: "8px",
								fontSize: "14px",
								"& .MuiSelect-select": { py: "7.5px" },
							}}
						>
							<MenuItem value="all">All Years</MenuItem>
							{years.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: { xs: 130, sm: 150 } }}>
						<InputLabel sx={{ fontSize: "14px" }}>Sort</InputLabel>
						<Select
							value={sort}
							label="Sort"
							onChange={(e) => setSort(e.target.value)}
							sx={{
								bgcolor: "#ffffff",
								borderRadius: "8px",
								fontSize: "14px",
								"& .MuiSelect-select": { py: "7.5px" },
							}}
						>
							<MenuItem value="newest">Newest First</MenuItem>
							<MenuItem value="oldest">Oldest First</MenuItem>
						</Select>
					</FormControl>

					<Button
						variant="text"
						onClick={clearFilters}
						size="small"
						sx={{
							textTransform: "none",
							color: "#64748b",
							fontSize: "13.5px",
							minWidth: "auto",
							px: 1,
							whiteSpace: "nowrap",
							"&:hover": { color: "#1e293b", bgcolor: "transparent" },
						}}
					>
						Clear
					</Button>
				</Box>
			</Box>

			<Box sx={{
				maxWidth: "1400px",
				mx: "auto",
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				mb: 2.5,
			}}>
				<Typography sx={{ fontWeight: 800, color: "#121E33", fontSize: "18px" }}>Magazines</Typography>
				<Chip label={filteredMagazines.length} size="small" sx={{ fontWeight: 700, background: "#e8f0fa", color: "#41516f" }} />
			</Box>

			{filteredMagazines.length > 0 ? (
				<Box sx={{
					maxWidth: "1400px",
					mx: "auto",
					display: "grid",
					gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(5, 1fr)" },
					gap: { xs: 1.5, sm: 2, md: 2.5 },
				}}>
					{filteredMagazines.map((magazine) => (
						<Card key={magazine.id} onClick={() => handleOpenMagazine(magazine)} sx={{
							cursor: "pointer",
							borderRadius: "12px",
							overflow: "hidden",
							border: "1px solid #e1e7ef",
							boxShadow: "none",
							transition: "all 0.25s ease",
							"&:hover": {
								transform: "translateY(-5px)",
								boxShadow: "0 12px 28px rgba(15, 23, 42, 0.10)",
								borderColor: "#c8d3e2",
							},
						}}>
							<Box sx={{ position: "relative", aspectRatio: "0.71", background: "#edf2f7", overflow: "hidden" }}>
								<Box component="img" src={magazine.thumbnail} alt={magazine.title} sx={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
									display: "block",
									transition: "transform 0.3s ease",
									"&:hover": { transform: "scale(1.025)" },
								}} />
							</Box>
							<CardContent sx={{
								p: { xs: 1.5, sm: 2 },
								"&:last-child": { pb: { xs: 1.5, sm: 2 } },
							}}>
								<Typography sx={{
									fontWeight: 800,
									color: "#121E33",
									fontSize: { xs: "13px", sm: "15px" },
								}}>{magazine.title}</Typography>
							</CardContent>
						</Card>
					))}
				</Box>
			) : (
				<Box sx={{ maxWidth: "1400px", mx: "auto", py: 10, textAlign: "center" }}>
					<MenuBookIcon sx={{ fontSize: 55, color: "#b5c0ce", mb: 2 }} />
					<Typography sx={{ fontWeight: 800, fontSize: "20px", color: "#334155" }}>No magazines found</Typography>
					<Typography sx={{ color: "#77869C", mt: 1 }}>Try changing your search or filters.</Typography>
					<Button onClick={clearFilters} sx={{ mt: 2, textTransform: "none" }}>Clear filters</Button>
				</Box>
			)}

			<Dialog
				open={openViewer}
				onClose={handleCloseViewer}
				maxWidth={false}
				fullScreen={isMobile}
				PaperProps={{
					sx: {
						width: isMobile ? "100vw" : "95vw",
						height: isMobile ? "100vh" : "94vh",
						maxWidth: "100vw",
						maxHeight: "100vh",
						bgcolor: "#f3f4f6",
						overflow: "hidden",
						borderRadius: isMobile ? 0 : "16px",
					},
				}}
			>
				<Box id="bjs-darpan-viewer-root" sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
					<Box
						sx={{
							height: 44,
							px: { xs: 1, md: 2 },
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							borderBottom: "1px solid rgba(148,163,184,0.25)",
							background: "linear-gradient(180deg,#f9fafb 0%,#eef2f7 100%)",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<Typography sx={{ color: "#1f2937", fontWeight: 700, fontSize: { xs: "0.9rem", md: "1rem" } }}>
								{selectedMagazine?.title ? `BJS Darpan • ${selectedMagazine.title}` : "BJS Darpan"}
							</Typography>
							{readMode ? <Chip size="small" color="warning" label="Read Mode" /> : null}
						</Box>
						<IconButton onClick={handleCloseViewer} sx={{ color: "#334155" }}>
							<CloseIcon />
						</IconButton>
					</Box>

					<DialogContent
						sx={{
							p: 0,
							width: "100%",
							height: "calc(100% - 44px)",
							overflow: readMode ? "auto" : "hidden",
							bgcolor: "#eef2f7",
							position: "relative",
						}}
					>
						{isLoading ? (
							<Box sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 20 }}>
								<LinearProgress variant={progress ? "determinate" : "indeterminate"} value={progress} />
							</Box>
						) : null}

						{error ? (
							<Typography sx={{ color: "#b91c1c", p: 3 }}>
								{error}
							</Typography>
						) : null}

						{!error && !pdfDoc ? (
							<Box
								sx={{
									minHeight: "100%",
									display: "flex",
									alignItems: "flex-start",
									justifyContent: "center",
									gap: readMode || isSinglePageFlip ? 0 : 0.5,
									pt: 2,
									px: 1,
								}}
							>
								<Box
									sx={{
										width: `${pageWidth}px`,
										height: `${Math.round(pageWidth * 1.42)}px`,
										border: "1px solid #dbe6f5",
										overflow: "hidden",
									}}
								>
									<DarpanPageSkeleton />
								</Box>
								{!readMode && !isSinglePageFlip ? (
									<Box
										sx={{
											width: `${pageWidth}px`,
											height: `${Math.round(pageWidth * 1.42)}px`,
											border: "1px solid #dbe6f5",
											overflow: "hidden",
										}}
									>
										<DarpanPageSkeleton />
									</Box>
								) : null}
							</Box>
						) : null}

						{pdfDoc ? (
							<>
								{readMode ? (
									<Box
										sx={{
											minHeight: "100%",
											display: "flex",
											alignItems: "flex-start",
											justifyContent: "center",
											pb: 2,
											pt: 2,
											px: 1,
										}}
									>
										<PdfCanvasPage
											pdfDoc={pdfDoc}
											pageNumber={currentPage}
											width={pageWidth}
											shouldRender
											cacheRef={cacheRef}
											onRendered={() => { }}
											quality={isLoading ? "fast" : "balanced"}
											clickable
											onPageClick={(event) => {
												const rect = event.currentTarget.getBoundingClientRect();
												const clickX = event.clientX - rect.left;
												const isLeftHalf = clickX < rect.width / 2;
												if (isLeftHalf) {
													goPrevPage();
												} else {
													goNextPage();
												}
											}}
										/>
									</Box>
								) : (
									<Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", pb: 2, px: 1 }}>
										<HTMLFlipBook
											key={`flipbook-${isSinglePageFlip ? "single" : "spread"}-${pageWidth}`}
											ref={flipbookRef}
											width={pageWidth}
											height={Math.round(pageWidth * 1.42)}
											minWidth={240}
											maxWidth={1400}
											minHeight={340}
											maxHeight={2000}
											size="fixed"
											autoSize={false}
											showCover={true}
											mobileScrollSupport
											maxShadowOpacity={0.4}
											drawShadow
											flippingTime={650}
											usePortrait={isSinglePageFlip}
											useMouseEvents
											showPageCorners
											startPage={Math.max(0, currentPage - 1)}
											onFlip={(e) => setCurrentPage((e?.data || 0) + 1)}
										>
											{Array.from({ length: numPages }).map((_, idx) => {
												const pageNum = idx + 1;
												const shouldRender = virtualPages.has(pageNum);
												return (
													<PdfCanvasPage
														key={`flip-page-${pageNum}`}
														pdfDoc={pdfDoc}
														pageNumber={pageNum}
														width={pageWidth}
														shouldRender={shouldRender}
														cacheRef={cacheRef}
														onRendered={() => { }}
														quality={isLoading ? "fast" : "balanced"}
													/>
												);
											})}
										</HTMLFlipBook>
									</Box>
								)}
							</>
						) : null}

						{pdfDoc ? (
							<Box
								sx={{
									position: "sticky",
									left: 0,
									right: 0,
									bottom: 12,
									zIndex: 35,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 0.5,
									px: 1,
									py: 0.6,
									width: "fit-content",
									mx: "auto",
									mt: 1,
									borderRadius: "10px",
									bgcolor: "rgba(238,241,247,0.96)",
									boxShadow: "0 10px 24px rgba(2,6,23,0.25)",
									border: "1px solid rgba(148,163,184,0.55)",
								}}
							>
								<Typography sx={{ color: "#4b5563", fontSize: "0.82rem", minWidth: 64, textAlign: "center" }}>
									{currentPage}/{numPages || 0}
								</Typography>
								<IconButton size="small" onClick={goPrevPage} disabled={currentPage <= 1} sx={{ color: "#41516f" }}>
									<ChevronLeftIcon fontSize="small" />
								</IconButton>
								<IconButton size="small" onClick={() => setZoomFactor((z) => Math.max(0.5, +(z * 0.9).toFixed(2)))} sx={{ color: "#41516f" }}>
									<ZoomOutIcon fontSize="small" />
								</IconButton>
								<IconButton size="small" onClick={() => setZoomFactor((z) => Math.min(2.5, +(z * 1.1).toFixed(2)))} sx={{ color: "#41516f" }}>
									<ZoomInIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={() => setReadMode((m) => !m)}
									sx={{ color: readMode ? "#1d4ed8" : "#41516f" }}
								>
									{readMode ? <ImportContactsIcon fontSize="small" /> : <MenuBookIcon fontSize="small" />}
								</IconButton>
								<IconButton size="small" onClick={toggleFullscreen} sx={{ color: "#41516f" }}>
									{isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
								</IconButton>
								<IconButton size="small" onClick={shareCurrentPdf} sx={{ color: "#41516f" }}>
									<ShareIcon fontSize="small" />
								</IconButton>
								<IconButton size="small" onClick={downloadCurrentPdf} sx={{ color: "#41516f" }}>
									<DownloadIcon fontSize="small" />
								</IconButton>
								<IconButton size="small" onClick={goNextPage} disabled={currentPage >= numPages} sx={{ color: "#41516f" }}>
									<ChevronRightIcon fontSize="small" />
								</IconButton>
							</Box>
						) : null}
					</DialogContent>
				</Box>
			</Dialog>
		</Box>
	);
};

export default LegacyFlipBookPage;
